/**
 * Textos legais (Termos de Uso e Política de Privacidade), em português.
 *
 * São documentos jurídicos redigidos por nós, renderizados pelo componente
 * `Markdown` (##/###, listas, **negrito**, `código`, e links `[texto](url)`).
 * As páginas `TermsPage`/`PrivacyPage` colocam o título (h1) via `.page-head`,
 * então cada texto começa direto no parágrafo de introdução — sem `#`/`##` de
 * nível de página.
 *
 * Contato do titular de dados: contact@gameofbrasil.com.
 */

/** Data da última revisão dos documentos (exibida no topo de cada página). */
export const LEGAL_UPDATED = '25 de julho de 2026';

/** E-mail de contato para questões legais e de privacidade (LGPD). */
export const LEGAL_CONTACT = 'contact@gameofbrasil.com';

export const TERMS_MD = `
Bem-vindo(a) ao **Game of Brasil — Batalha de Territórios** ("Jogo"). Estes
Termos de Uso ("Termos") regem o seu acesso e uso do Jogo, do site e dos
serviços relacionados (em conjunto, o "Serviço"), operados pela equipe do Game
of Brasil ("nós", "nosso"). Ao acessar, criar uma conta, entrar como convidado
ou usar o Serviço de qualquer forma, você concorda com estes Termos e com a
[Política de Privacidade](/privacidade). Se você não concorda, não use o
Serviço.

## 1. Aceitação e alterações

Ao usar o Serviço você declara ter lido, compreendido e aceitado estes Termos.
Podemos alterá-los a qualquer momento; a versão vigente estará sempre disponível
nesta página, com a data de atualização no topo. Mudanças relevantes poderão ser
comunicadas dentro do Jogo. O uso continuado após a publicação de uma nova versão
significa a aceitação dela.

## 2. Elegibilidade e idade

O Serviço destina-se a maiores de **13 anos**. Se a idade mínima exigida no seu
país ou pela loja de aplicativos (Google Play, App Store) for superior, prevalece
a mais alta. Se você for menor de idade, o uso deve contar com o consentimento e a
supervisão de um responsável legal. Ao usar o Serviço, você declara cumprir esses
requisitos.

## 3. Aviso de sátira política

O Game of Brasil é uma **obra de sátira e paródia política**. As cartas,
personagens, nomes, facções, territórios, ilustrações e textos são **caricaturas
ficcionais**, criadas para fins de humor, crítica social e comentário político.

- O conteúdo **não representa** declarações, opiniões, apoio, patrocínio ou
  qualquer vínculo real de pessoas físicas, agentes públicos, partidos,
  instituições, empresas ou marcas efetivamente existentes.
- Qualquer semelhança é utilizada como recurso de **paródia e expressão
  artística**, protegida pela liberdade de expressão (Constituição Federal,
  art. 5º, IV e IX).
- O Jogo **não** tem filiação partidária e **não** constitui propaganda
  eleitoral, endosso ou desendosso de qualquer candidatura, agremiação ou
  autoridade.

## 4. Sua conta

Você pode jogar como **convidado** (escolhendo um nome de jogador) ou vincular
uma conta de terceiros (Google, Apple ou Discord). Você é responsável pela
atividade realizada na sua conta e por manter suas credenciais seguras. O nome de
jogador e demais informações de perfil não podem violar direitos de terceiros nem
estas regras (ver seção 5).

## 5. Conduta do usuário

Ao usar o Serviço, você concorda em **não**:

- adotar nome de jogador, mensagem ou conteúdo ilegal, ofensivo, difamatório,
  discriminatório, de incitação ao ódio ou à violência;
- assediar, ameaçar ou personificar outras pessoas;
- trapacear, usar bots, exploits, engenharia reversa, automação ou qualquer meio
  para obter vantagem indevida ou fraudar o sistema;
- tentar burlar mecanismos antifraude (incluindo a verificação anti-robô) ou
  criar contas em massa;
- interferir na operação, segurança ou infraestrutura do Serviço, ou acessar
  dados de outros usuários sem autorização.

## 6. Bens virtuais e compras

O Jogo pode oferecer moedas, pacotes de cartas e outros itens virtuais ("Bens
Virtuais"). Os Bens Virtuais:

- são uma **licença limitada, pessoal, intransferível e revogável** de uso dentro
  do Jogo, e **não** constituem propriedade nem têm valor monetário real;
- **não** podem ser trocados por dinheiro, transferidos ou sacados para fora do
  Jogo;
- podem ter preço, disponibilidade e mecânica alterados por nós a qualquer tempo.

Compras eventuais são processadas pela respectiva loja de aplicativos, sujeitas
às políticas dela. Salvo exigência legal aplicável (incluindo o Código de Defesa
do Consumidor e o direito de arrependimento, quando cabível), os valores pagos por
Bens Virtuais **não são reembolsáveis**.

## 7. Propriedade intelectual

Todo o conteúdo do Serviço — código, arte, textos, marcas, design das cartas e
territórios — é de nossa titularidade ou licenciado a nós, e protegido por lei.
Concedemos a você uma licença limitada, pessoal e não exclusiva para usar o
Serviço conforme estes Termos. Você não pode copiar, distribuir, modificar ou
criar obras derivadas sem autorização, ressalvados os usos permitidos por lei.

## 8. Isenções e limitação de responsabilidade

O Serviço é fornecido **"no estado em que se encontra"**, sem garantias de
disponibilidade ininterrupta, ausência de erros ou adequação a um fim específico.
Na máxima extensão permitida pela lei aplicável, não nos responsabilizamos por
danos indiretos, incidentais ou lucros cessantes decorrentes do uso ou da
impossibilidade de uso do Serviço. Nada nestes Termos exclui responsabilidades que
não possam ser afastadas por lei.

## 9. Suspensão e encerramento

Podemos suspender ou encerrar o seu acesso, no todo ou em parte, em caso de
violação destes Termos, de exigência legal ou de risco à segurança do Serviço. Você
pode deixar de usar o Serviço a qualquer momento e solicitar a exclusão dos seus
dados conforme a [Política de Privacidade](/privacidade).

## 10. Lei aplicável e foro

Estes Termos são regidos pelas leis da **República Federativa do Brasil**. Fica
eleito o foro do domicílio do usuário para dirimir eventuais controvérsias, quando
aplicável a legislação consumerista.

## 11. Contato

Dúvidas sobre estes Termos podem ser enviadas para [contact@gameofbrasil.com](mailto:contact@gameofbrasil.com).
`;

export const PRIVACY_MD = `
Esta Política de Privacidade explica como coletamos, usamos, compartilhamos e
protegemos seus dados pessoais quando você usa o **Game of Brasil — Batalha de
Territórios** ("Jogo" ou "Serviço"), em conformidade com a **Lei Geral de
Proteção de Dados** (Lei nº 13.709/2018 — "LGPD"). Ao usar o Serviço, você também
concorda com os [Termos de Uso](/termos).

## 1. Quem é o controlador

O controlador dos dados é a equipe do Game of Brasil. Para exercer seus direitos
ou tirar dúvidas sobre privacidade, use o contato da seção 9.

## 2. Dados que coletamos

- **Cadastro de convidado:** o nome de jogador que você escolhe e um identificador
  de conta gerado automaticamente.
- **Login com terceiros (opcional):** ao vincular Google, Apple ou Discord,
  recebemos do provedor um identificador e, conforme sua autorização, e-mail e
  nome de perfil.
- **Dados de jogo:** progresso, partidas, estatísticas, coleção de cartas e uso de
  bens virtuais.
- **Dados técnicos e de log:** tipo de dispositivo, versão do app, endereço IP e
  registros de acesso, usados para operação, segurança e diagnóstico.
- **Antifraude:** um token da verificação anti-robô (Cloudflare Turnstile) gerado
  ao criar conta, usado para prevenir criação de contas em massa. Não coletamos
  dados biométricos.

Não coletamos intencionalmente dados sensíveis nem construímos perfis para
publicidade de terceiros.

## 3. Para que usamos os dados

- criar e autenticar sua conta e permitir que você jogue;
- operar, manter, personalizar e melhorar o Serviço;
- prevenir fraude, abuso e criação de contas em massa, e garantir a segurança;
- cumprir obrigações legais e responder a solicitações de autoridades.

## 4. Bases legais (LGPD, art. 7º)

Tratamos seus dados com fundamento na **execução de contrato** (prestar o
Serviço), no **legítimo interesse** (segurança, prevenção à fraude e melhoria do
produto), no **cumprimento de obrigação legal** e, quando aplicável, no seu
**consentimento** (por exemplo, ao vincular uma conta de terceiro).

## 5. Compartilhamento com operadores

Não vendemos seus dados pessoais. Compartilhamos dados apenas com prestadores que
processam informações em nosso nome, sob obrigação de confidencialidade e
segurança:

- **Hospedagem e backend:** Fly.io (aplicação) e MongoDB Atlas (banco de dados);
- **Segurança e rede:** Cloudflare (incluindo a verificação anti-robô Turnstile);
- **Autenticação:** Google, Apple e Discord, quando você opta por vincular uma
  dessas contas;
- **Lojas de aplicativos:** Google Play e App Store, para distribuição e eventuais
  compras.

Também poderemos compartilhar dados para cumprir a lei ou proteger direitos, na
forma da legislação.

## 6. Armazenamento local

Para manter você conectado, guardamos um **token de sessão** no armazenamento local
do dispositivo/navegador (por exemplo, \`localStorage\`). Ele é necessário ao
funcionamento do Serviço e pode ser removido ao encerrar a sessão ou limpar os
dados do app/navegador.

## 7. Retenção

Mantemos os dados enquanto sua conta existir e pelo tempo necessário às
finalidades desta Política ou ao cumprimento de obrigações legais. Após esse
período, os dados são eliminados ou anonimizados.

## 8. Seus direitos (LGPD, art. 18)

Você pode, a qualquer tempo, solicitar: confirmação de tratamento; acesso;
correção; anonimização, bloqueio ou eliminação de dados desnecessários;
portabilidade; informação sobre compartilhamentos; e revogação do consentimento.
Para exercê-los, use o contato da seção 9. Você também pode apresentar reclamação
à Autoridade Nacional de Proteção de Dados (ANPD).

## 9. Contato do encarregado

Para exercer seus direitos ou esclarecer dúvidas sobre privacidade, escreva para
[contact@gameofbrasil.com](mailto:contact@gameofbrasil.com).

## 10. Segurança

Adotamos medidas técnicas e organizacionais razoáveis para proteger seus dados
contra acesso não autorizado, perda ou alteração. Nenhum sistema é totalmente
seguro; em caso de incidente relevante, agiremos conforme a LGPD.

## 11. Crianças e adolescentes

O Serviço não se destina a menores de 13 anos. O tratamento de dados de crianças e
adolescentes observa o seu melhor interesse, nos termos da LGPD. Responsáveis que
identificarem uso indevido por um menor podem nos contatar para remoção dos dados.

## 12. Transferência internacional

Alguns operadores citados na seção 5 podem processar dados fora do Brasil. Nesses
casos, adotamos salvaguardas compatíveis com a LGPD para proteger seus dados.

## 13. Alterações desta Política

Podemos atualizar esta Política periodicamente. A versão vigente estará sempre
nesta página, com a data de atualização no topo; mudanças relevantes poderão ser
comunicadas no Jogo.
`;
